import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Bot,
  Search,
  Phone,
  Calendar,
  ArrowLeft,
  Clock,
  PhoneCall,
  MessageSquare,
  CircleAlert,
  Copy,
} from 'lucide-react';
import {
  getCalls,
  getMessages,
  getCustomerAgent,
  getCustomerAgents,
} from '../lib/api';
import { formatDate, formatDateTime, formatDuration } from '../lib/utils';
import {
  getAgentPlanName,
  getRemainingMessageCount,
  isAgentFacebookMessagingActive,
} from '../lib/subscriptionQuota';
import {
  normalizeVoiceMinutesQuotaForAgent,
  normalizeSmsQuotaForAgent,
  normalizeFacebookQuotaForAgent,
  getQuotaAlertLevel,
} from '../lib/usageMetrics';

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

const pickNumericField = (source, keys) => {
  if (!source) return null;
  for (const key of keys) {
    const value = toNumberOrNull(source[key]);
    if (value !== null) return value;
  }
  return null;
};

/** Prefer explicit call/voice keys — avoid bare `minutes_used`, which often maps to message quota. */
const nestedAgentSources = (agent) =>
  [agent, agent?.subscription, agent?.usage, agent?.stats, agent?.metrics].filter(Boolean);

const pickNumericAcrossAgent = (agent, keys) => {
  for (const source of nestedAgentSources(agent)) {
    const v = pickNumericField(source, keys);
    if (v !== null) return v;
  }
  return null;
};

const getAgentCallCount = (agent) =>
  pickNumericAcrossAgent(agent, [
    'call_count',
    'calls_count',
    'total_calls',
    'calls_total',
    'calls',
    'num_calls',
    'voice_calls_count',
    'completed_calls_count',
  ]);

const getAgentCallMinutes = (agent) =>
  pickNumericAcrossAgent(agent, [
    'call_minutes_used',
    'calls_minutes_used',
    'total_call_minutes',
    'voice_minutes_used',
    'minutes_used_for_calls',
    'call_minutes',
    'voice_call_minutes',
  ]);

const minutesFromCall = (call) => {
  if (call == null) return 0;
  if (call.minutes_used != null) return Number(call.minutes_used) || 0;
  if (call.duration_minutes != null) return Number(call.duration_minutes) || 0;
  if (call.duration_seconds != null) return (Number(call.duration_seconds) || 0) / 60;
  return 0;
};

const buildAgentMapFromCalls = (calls = []) => {
  const map = new Map();
  calls.forEach((call) => {
    const agent = call.agent || {};
    const agentId = agent.agent_id || call.agent_id;
    if (!agentId) return;
    if (!map.has(agentId)) {
      map.set(agentId, {
        agent_id: agentId,
        name: agent.name || `Agent ${agentId.slice(-6)}`,
        status: agent.status || 'active',
        phone_number: agent.phone_number || null,
        created_at: agent.created_at || null,
        subscription: agent.subscription || null,
        call_minutes_used: 0,
        call_count: 0,
      });
    }
    const existing = map.get(agentId);
    existing.call_count += 1;
    existing.call_minutes_used += minutesFromCall(call);
    if (!existing.name && agent.name) existing.name = agent.name;
  });
  return Array.from(map.values());
};

/** Remaining below 10% of allocated capacity (same rule as quota “critical”). */
const isBelowTenPercentRemaining = (remaining, total) =>
  getQuotaAlertLevel(remaining, total) === 'critical';

function AgentQuotaSummaryBlock({ agent, attentionTestId, isolateActions = false }) {
  const agentId = agent?.agent_id;
  if (!agentId) return null;

  const voice = normalizeVoiceMinutesQuotaForAgent(agent);
  const sms = normalizeSmsQuotaForAgent(agent);
  const socialOn = isAgentFacebookMessagingActive(agent);
  const social = socialOn ? normalizeFacebookQuotaForAgent(agent) : { remaining: null, total: null };
  const lowMinutes = isBelowTenPercentRemaining(voice.remaining, voice.total);
  const lowMessages = isBelowTenPercentRemaining(sms.remaining, sms.total);
  const lowSocial =
    socialOn && social.total > 0 && isBelowTenPercentRemaining(social.remaining, social.total);
  const needsAttention = lowMinutes || lowMessages || lowSocial;
  const packPurchaseTo = `/buy-minutes?tab=packages&agentId=${encodeURIComponent(agentId)}`;
  const minutesNum =
    voice.remaining != null && Number.isFinite(voice.remaining)
      ? Math.max(0, Math.round(voice.remaining)).toLocaleString()
      : '—';
  const messagesNum =
    sms.remaining != null && Number.isFinite(sms.remaining)
      ? Math.max(0, Math.round(sms.remaining)).toLocaleString()
      : '—';
  const socialNum =
    socialOn && social.remaining != null && Number.isFinite(social.remaining)
      ? Math.max(0, Math.round(social.remaining)).toLocaleString()
      : '—';

  return (
    <div className="space-y-3">
      <div>
        <p className="text-xs text-slate-500">Subscription plan name</p>
        <p className="text-sm font-semibold text-slate-900 truncate">{getAgentPlanName(agent) || '—'}</p>
      </div>
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
        <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
          <div className="min-w-[5.5rem]">
            <p className="text-xs font-medium text-slate-800 leading-snug">Voice minutes</p>
            <p className="text-lg font-bold text-slate-900 tabular-nums leading-tight mt-1">{minutesNum}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">Remaining</p>
          </div>
          <div className="min-w-[5.5rem]">
            <p className="text-xs font-medium text-slate-800 leading-snug">SMS &amp; chat</p>
            <p className="text-lg font-bold text-slate-900 tabular-nums leading-tight mt-1">{messagesNum}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">Remaining</p>
          </div>
          <div className="min-w-[5.5rem]">
            <p className="text-xs font-medium text-slate-800 leading-snug">Social messaging</p>
            <p className="text-lg font-bold text-slate-900 tabular-nums leading-tight mt-1">{socialNum}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">
              {socialOn ? 'Remaining' : 'Not on this plan'}
            </p>
          </div>
        </div>
      </div>
      {needsAttention ? (
        <Link
          to={packPurchaseTo}
          onClick={isolateActions ? (e) => e.stopPropagation() : undefined}
          className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-800 hover:bg-red-100 transition-colors w-fit"
          data-testid={attentionTestId || `agent-low-quota-link-${agentId}`}
          title="Add minute packs or prepaid capacity"
        >
          <CircleAlert className="w-4 h-4 shrink-0" aria-hidden />
          Attention — add packages
        </Link>
      ) : null}
    </div>
  );
}

const computeCallAggregatesByAgentId = (calls = []) => {
  const map = new Map();
  calls.forEach((call) => {
    const agentId = call.agent?.agent_id || call.agent_id;
    if (!agentId) return;
    if (!map.has(agentId)) {
      map.set(agentId, { call_count: 0, call_minutes_used: 0 });
    }
    const row = map.get(agentId);
    row.call_count += 1;
    row.call_minutes_used += minutesFromCall(call);
  });
  return map;
};

/** When the agents API omits aggregates, derive counts from the calls list (same source as detail page). */
const enrichAgentsWithCallAggregates = (agents, calls) => {
  const byAgent = computeCallAggregatesByAgentId(calls);
  return agents.map((agent) => {
    const agg = byAgent.get(agent.agent_id) || { call_count: 0, call_minutes_used: 0 };
    const next = { ...agent };
    if (getAgentCallCount(next) === null) {
      next.call_count = agg.call_count;
    }
    if (getAgentCallMinutes(next) === null) {
      next.call_minutes_used = agg.call_minutes_used;
    }
    return next;
  });
};

export function AgentsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      setLoading(true);
      const response = await getCustomerAgents({ limit: 200 });
      const list = Array.isArray(response?.data?.agents) ? response.data.agents : [];
      let merged = list;
      try {
        const callsRes = await getCalls({ limit: 1000, skip: 0 });
        const calls = Array.isArray(callsRes?.data?.calls) ? callsRes.data.calls : [];
        merged = enrichAgentsWithCallAggregates(list, calls);
      } catch {
        // Keep agent list without call rollups if /calls fails
      }
      setAgents(merged);
    } catch (error) {
      try {
        const callsRes = await getCalls({ limit: 200, skip: 0 });
        const calls = Array.isArray(callsRes?.data?.calls) ? callsRes.data.calls : [];
        setAgents(buildAgentMapFromCalls(calls));
      } catch {
        toast.error('Failed to load agents');
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredAgents = useMemo(() => {
    if (!search) return agents;
    const normalized = search.toLowerCase();
    return agents.filter((agent) =>
      (agent.name || '').toLowerCase().includes(normalized) ||
      (agent.agent_id || '').toLowerCase().includes(normalized)
    );
  }, [agents, search]);

  return (
    <div className="space-y-6" data-testid="agents-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-jakarta font-bold text-slate-900">My Agents</h1>
          <p className="text-slate-500 mt-1">Track subscriptions, message usage, and call activity by agent</p>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search by name or Agent ID..."
          className="pl-10"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}><CardContent className="p-6"><Skeleton className="h-24" /></CardContent></Card>
          ))}
        </div>
      ) : filteredAgents.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Bot className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No agents found</h3>
            <p className="text-slate-500">Your agents will appear here once configured.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredAgents.map((agent) => (
            <Card
              key={agent.agent_id}
              role="button"
              tabIndex={0}
              className="hover:shadow-md transition-shadow cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
              onClick={() => navigate(`/agents/${agent.agent_id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/agents/${agent.agent_id}`);
                }
              }}
              data-testid={`agent-list-card-${agent.agent_id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                    <Bot className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div className="space-y-3 flex-1 min-w-0">
                    <div className="flex flex-wrap items-center justify-between gap-2 gap-y-1">
                      <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <h3 className="text-lg font-semibold text-slate-900 truncate">{agent.name}</h3>
                        <Badge variant={agent.status === 'active' ? 'default' : 'outline'}>
                          {agent.status || 'active'}
                        </Badge>
                      </div>
                      <div
                        className="flex items-center gap-0.5 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        role="presentation"
                      >
                        <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-600" asChild title="Booking calendar">
                          <Link
                            to={`/agents/${agent.agent_id}/calendar`}
                            aria-label="Open booking calendar"
                            data-testid={`agent-list-calendar-${agent.agent_id}`}
                          >
                            <Calendar className="w-4 h-4" />
                          </Link>
                        </Button>
                        <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-600" asChild title="View calls for this agent">
                          <Link
                            to={`/calls?agentId=${encodeURIComponent(agent.agent_id)}`}
                            aria-label="View all calls for this agent"
                            data-testid={`agent-list-calls-${agent.agent_id}`}
                          >
                            <PhoneCall className="w-4 h-4" />
                          </Link>
                        </Button>
                        <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-600" asChild title="View messages for this agent">
                          <Link
                            to={`/messages?agentId=${encodeURIComponent(agent.agent_id)}`}
                            aria-label="View all messages for this agent"
                            data-testid={`agent-list-messages-${agent.agent_id}`}
                          >
                            <MessageSquare className="w-4 h-4" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-slate-500">Agent ID: {agent.agent_id}</p>
                    <AgentQuotaSummaryBlock agent={agent} isolateActions />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export function AgentDetailPage() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);
  const [calls, setCalls] = useState([]);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    loadData();
  }, [agentId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [agentRes, callsRes, messagesRes] = await Promise.all([
        getCustomerAgent(agentId),
        getCalls({ limit: 50, skip: 0, agent_id: agentId, agentId }),
        getMessages({ limit: 50, skip: 0, agent_id: agentId, agentId }).catch(() => ({
          data: { messages: [], total: 0 },
        })),
      ]);
      setAgent(agentRes?.data?.agent || agentRes?.data || null);
      setCalls(Array.isArray(callsRes?.data?.calls) ? callsRes.data.calls : []);
      setMessages(Array.isArray(messagesRes?.data?.messages) ? messagesRes.data.messages : []);
    } catch (error) {
      try {
        let matched = null;
        try {
          const agentsRes = await getCustomerAgents({ limit: 200 });
          const list = Array.isArray(agentsRes?.data?.agents) ? agentsRes.data.agents : [];
          matched = list.find((a) => a.agent_id === agentId) || null;
        } catch {
          // Ignore and continue with calls fallback
        }
        const [callsRes, messagesRes] = await Promise.all([
          getCalls({ limit: 200, skip: 0 }),
          getMessages({ limit: 200, skip: 0 }).catch(() => ({ data: { messages: [] } })),
        ]);
        const allCalls = Array.isArray(callsRes?.data?.calls) ? callsRes.data.calls : [];
        const allMessages = Array.isArray(messagesRes?.data?.messages) ? messagesRes.data.messages : [];
        if (!matched) {
          const list = buildAgentMapFromCalls(allCalls);
          matched = list.find((a) => a.agent_id === agentId) || null;
        }
        setAgent(matched);
        const filtered = allCalls.filter((call) => (call.agent?.agent_id || call.agent_id) === agentId);
        setCalls(filtered.slice(0, 50));
        const filteredMsgs = allMessages.filter((m) => (m.agent?.agent_id || m.agent_id) === agentId);
        setMessages(filteredMsgs.slice(0, 50));
      } catch {
        toast.error('Failed to load agent details');
        navigate('/agents');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-40" />
        <Card><CardContent className="p-6"><Skeleton className="h-32" /></CardContent></Card>
      </div>
    );
  }

  if (!agent) return null;

  const remainingMessages = getRemainingMessageCount(agent);
  const hasActiveSubscription = Boolean(getAgentPlanName(agent));
  const shouldShowPurchaseOptions =
    !hasActiveSubscription || (remainingMessages !== null && remainingMessages <= 0);

  return (
    <div className="space-y-6" data-testid="agent-detail-page">
      <Button variant="ghost" onClick={() => navigate('/agents')}>
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Agents
      </Button>

      <Card>
        <CardHeader className="space-y-1 pb-2">
          <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-xl">
            <span className="flex items-center gap-2 min-w-0">
              <Bot className="w-5 h-5 text-indigo-600 shrink-0" />
              <span className="truncate">{agent.name}</span>
            </span>
            <div className="flex items-center gap-0.5 shrink-0">
              <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-600" asChild title="Booking calendar">
                <Link to={`/agents/${agent.agent_id}/calendar`} aria-label="Open booking calendar">
                  <Calendar className="w-4 h-4" />
                </Link>
              </Button>
              <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-600" asChild title="View calls for this agent">
                <Link to={`/calls?agentId=${encodeURIComponent(agent.agent_id)}`} aria-label="View all calls for this agent">
                  <PhoneCall className="w-4 h-4" />
                </Link>
              </Button>
              <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-600" asChild title="View messages for this agent">
                <Link to={`/messages?agentId=${encodeURIComponent(agent.agent_id)}`} aria-label="View all messages for this agent">
                  <MessageSquare className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Badge variant={agent.status === 'active' ? 'default' : 'outline'}>{agent.status || 'active'}</Badge>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
            <span className="text-slate-500 shrink-0">Agent ID</span>
            <code className="text-xs bg-slate-100 text-slate-800 px-2 py-1 rounded-md font-mono break-all">
              {agent.agent_id}
            </code>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8 shrink-0"
              title="Copy agent ID"
              aria-label="Copy agent ID"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(agent.agent_id);
                  toast.success('Agent ID copied');
                } catch {
                  toast.error('Could not copy');
                }
              }}
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          {agent.vapi_agent_id && <p className="text-sm text-slate-600">VAPI Agent ID: {agent.vapi_agent_id}</p>}
          {agent.phone_number && (
            <p className="text-sm text-slate-600 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              {agent.phone_number}
            </p>
          )}
          <AgentQuotaSummaryBlock agent={agent} attentionTestId={`agent-detail-low-quota-link-${agent.agent_id}`} />
          {agent.created_at && (
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Created {formatDate(agent.created_at)}
            </p>
          )}
          {shouldShowPurchaseOptions && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
              <p className="text-sm text-amber-800 font-medium">
                This agent does not currently have enough minutes to handle calls reliably.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button asChild className="bg-teal-600 hover:bg-teal-700">
                  <Link to={`/pricing?tab=plans&agentId=${agent.agent_id}`}>Buy Subscription</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to={`/buy-minutes?tab=packages&agentId=${encodeURIComponent(agent.agent_id)}`}>
                    Buy Minutes
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Calls</CardTitle>
        </CardHeader>
        <CardContent>
          {calls.length === 0 ? (
            <p className="text-sm text-slate-500">No calls found for this agent.</p>
          ) : (
            <div className="space-y-3">
              {calls.slice(0, 10).map((call) => (
                <Link
                  key={call.call_id}
                  to={`/calls/${call.call_id}`}
                  className="block rounded-lg border border-slate-200 p-3 hover:bg-slate-50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-slate-900">Call #{call.call_id.slice(-8)}</p>
                      <p className="text-xs text-slate-500">{formatDateTime(call.created_at || call.started_at)}</p>
                      {call.summary && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{call.summary}</p>}
                    </div>
                    <div className="text-right text-xs text-slate-500 space-y-1">
                      <p className="flex items-center justify-end gap-1"><PhoneCall className="w-3 h-3" /> {call.status}</p>
                      <p className="flex items-center justify-end gap-1"><Clock className="w-3 h-3" /> {formatDuration(call.duration_seconds || 0)}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Messages</CardTitle>
        </CardHeader>
        <CardContent>
          {messages.length === 0 ? (
            <p className="text-sm text-slate-500">No messages found for this agent.</p>
          ) : (
            <div className="space-y-3">
              {messages.slice(0, 10).map((row) => {
                const mid = row.message_id || row.messageId || row.id;
                return (
                  <Link
                    key={mid}
                    to={`/messages/${encodeURIComponent(mid)}`}
                    className="block rounded-lg border border-slate-200 p-3 hover:bg-slate-50"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 flex items-center gap-2">
                          <MessageSquare className="w-4 h-4 text-cyan-600 flex-shrink-0" />
                          Message #{mid && mid.length > 8 ? mid.slice(-8) : mid}
                        </p>
                        <p className="text-xs text-slate-500">{formatDateTime(row.created_at)}</p>
                        {row.preview && (
                          <p className="text-sm text-slate-600 mt-1 line-clamp-2">{row.preview}</p>
                        )}
                      </div>
                      <div className="text-right text-xs text-slate-500 space-y-1 flex-shrink-0">
                        <p>{row.status || '—'}</p>
                        <p className="capitalize">{String(row.channel || '').toLowerCase() || '—'}</p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
